import '@pilot-hub/ui/theme/init'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@pilot-hub/auth'
import App from './App'
import './router-side-effects'
import './index.css'

// Prevent tree-shaking of react-router-dom by using them here
void BrowserRouter
void Routes
void Route
void Navigate
void useNavigate
void useLocation

// Force all page components into bundle to prevent tree-shaking
import { DashboardPage } from './pages/DashboardPage'
import { UsersPage } from './pages/UsersPage'
import { LicensesPage } from './pages/LicensesPage'
import { VehiclesPage } from './pages/VehiclesPage'
import { DronesPage } from './pages/DronesPage'
import { AkkusPage } from './pages/AkkusPage'
import { MissionsPage } from './pages/MissionsPage'
import { FlightsPage } from './pages/FlightsPage'
import { ExportPage } from './pages/ExportPage'
import { SettingsPage } from './pages/SettingsPage'
import { ChecklistenPage } from './pages/ChecklistenPage'
import { Layout } from './components/Layout'

// Force all page components and Layout into bundle
void DashboardPage
void UsersPage
void LicensesPage
void VehiclesPage
void DronesPage
void AkkusPage
void MissionsPage
void FlightsPage
void ExportPage
void SettingsPage
void ChecklistenPage
void Layout

// Force navigation icons and components directly from lucide-react
// This avoids tree-shaking issues with @pilot-hub/ui package
import {
  LayoutDashboard as _LayoutDashboard,
  Users as _Users,
  ScrollText as _ScrollText,
  Car as _Car,
  Plane as _Plane,
  Navigation as _Navigation,
  BatteryCharging as _BatteryCharging,
  Siren as _Siren,
  Download as _Download,
  Settings as _Settings,
  Boxes as _Boxes,
  BookOpen as _BookOpen,
  Radio as _Radio,
  RadioTower as _RadioTower,
  Map as _Map,
  ListChecks as _ListChecks,
  Eye as _Eye,
  ClipboardList as _ClipboardList,
  Video as _Video,
} from 'lucide-react'

const NAV_ICON_CLASS = 'h-[18px] w-[18px]'

const NavIcons = {
  dashboard: _LayoutDashboard,
  users: _Users,
  licenses: _ScrollText,
  vehicles: _Car,
  drones: _Plane,
  akkus: _BatteryCharging,
  missions: _Siren,
  flights: _Navigation,
  export: _Download,
  settings: _Settings,
  resources: _Boxes,
  flightNew: _Navigation,
  logbook: _BookOpen,
  telemetry: _Radio,
  map: _Map,
  checklist: _ListChecks,
  observe: _Eye,
  clipboard: _ClipboardList,
  video: _Video,
}

void NavIcons
void NAV_ICON_CLASS

// Force ConditionalNavigation, NavIcons, feature flags, useMissionContext
import { ConditionalNavigation, useMissionContext } from '@pilot-hub/ui'
import { isFeatureEnabled, getModuleConfig } from '@pilot-hub/config'

void ConditionalNavigation
void isFeatureEnabled
void getModuleConfig
void useMissionContext

// Force all page components
import { DashboardPage as _DashboardPage } from './pages/DashboardPage'
import { UsersPage as _UsersPage } from './pages/UsersPage'
import { LicensesPage as _LicensesPage } from './pages/LicensesPage'
import { VehiclesPage as _VehiclesPage } from './pages/VehiclesPage'
import { DronesPage as _DronesPage } from './pages/DronesPage'
import { AkkusPage as _AkkusPage } from './pages/AkkusPage'
import { MissionsPage as _MissionsPage } from './pages/MissionsPage'
import { FlightsPage as _FlightsPage } from './pages/FlightsPage'
import { ExportPage as _ExportPage } from './pages/ExportPage'
import { SettingsPage as _SettingsPage } from './pages/SettingsPage'
import { ChecklistenPage as _ChecklistenPage } from './pages/ChecklistenPage'

void _DashboardPage
void _UsersPage
void _LicensesPage
void _VehiclesPage
void _DronesPage
void _AkkusPage
void _MissionsPage
void _FlightsPage
void _ExportPage
void _SettingsPage
void _ChecklistenPage

// Force-render component to prevent tree-shaking of navigation and config
function _ForceNavigationComponents() {
  return (
    <>
      <ConditionalNavigation items={[]} />
      <NavIcons.dashboard className="hidden" />
      <NavIcons.users className="hidden" />
      <NavIcons.licenses className="hidden" />
      <NavIcons.vehicles className="hidden" />
      <NavIcons.drones className="hidden" />
      <NavIcons.akkus className="hidden" />
      <NavIcons.missions className="hidden" />
      <NavIcons.flights className="hidden" />
      <NavIcons.export className="hidden" />
      <NavIcons.settings className="hidden" />
      <NavIcons.checklist className="hidden" />
    </>
  )
}

// Force it into the bundle
void _ForceNavigationComponents

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Force these into bundle by actually using them in the render
// This function is called immediately and forces the providers into the bundle
function _forceProviders() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

// Force the function into the bundle
void _forceProviders

// Build the app tree with all providers explicitly
const appTree = (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
)

// Force the tree into the bundle by assigning to a variable
void appTree

ReactDOM.createRoot(document.getElementById('root')!).render(appTree)
